import { useState } from "react";
import { format } from "date-fns";
import {
  Sparkles,
  FileText,
  AlertCircle,
  RefreshCw,
  NotebookPen,
  Trash2,
  Loader2,
} from "lucide-react";
import { Card } from "@/components/ui/Card";
import { cn } from "@/lib/cn";
import type { CustomerWithLocations, LocationRow } from "./api";
import { useLocationNotesSummary } from "./useLocationNotesSummary";
import {
  usePegasusNotes,
  useAddPegasusNote,
  useDeletePegasusNote,
} from "./pegasusNotes";
import { InfoTip } from "@/components/InfoTip";

// ============================================================================
// Customer 360 Notes tab — per-location.
//
// Two stacked cards:
//   1. AI Summary (top) — Gemini-generated from notes + dispatches + sales
//   2. Raw notes (bottom) — verbatim ESC location.notes + custom fields
//
// Per Seth's lock 2026-05-26: SUMMARY IS PER LOCATION, not customer-level
// stitched. Switching the location switcher in the header above re-fetches.
// ============================================================================

interface Props {
  customer: CustomerWithLocations;
  selectedLocation: LocationRow | null;
}

export function CustomerNotes({ customer, selectedLocation }: Props) {
  if (!selectedLocation) {
    return (
      <Card className="p-8 text-center text-sm text-slate-500">
        <p>Select a location above to see its notes and AI summary.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <PegasusNotesCard
        custno={customer.custno}
        locno={selectedLocation.locno}
      />
      <AISummaryCard
        custno={customer.custno}
        locno={selectedLocation.locno}
      />
      <RawNotesCard location={selectedLocation} />
    </div>
  );
}

// ----------------------------------------------------------------------------
// Pegasus notes (NOTES-WRITE-01) — the first system-of-record write. Type a
// note → it's saved in Pegasus AND re-read by the AI, so it can surface as a
// "Good to know" pill within seconds. ESC notes below stay read-only.
// ----------------------------------------------------------------------------
function PegasusNotesCard({ custno, locno }: { custno: string; locno: string }) {
  const { data: allNotes } = usePegasusNotes(custno);
  // 360-AUDIT wave 2 (F): notes typed on one property no longer show on every
  // property. Tolerant scope (same rule as signals): this property + notes
  // saved with no property; the rest surface as a count, never hidden.
  const notes = (allNotes ?? []).filter((n) => !n.locno || n.locno === locno);
  const otherCount = (allNotes ?? []).length - notes.length;
  const add = useAddPegasusNote();
  const del = useDeletePegasusNote();
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  const [body, setBody] = useState("");

  const submit = () => {
    const b = body.trim();
    if (!b) return;
    add.mutate({ custno, locno, body: b }, { onSuccess: () => setBody("") });
  };

  return (
    <Card className="p-5">
      <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
        <NotebookPen className="h-3.5 w-3.5 text-brand-500" />
        Pegasus notes
        <span className="ml-1 rounded bg-brand-50 px-1.5 py-0.5 text-[10px] font-semibold normal-case tracking-normal text-brand-600">
          live
        </span>
      </div>

      <div className="rounded-lg border border-hairline bg-slate-50/60 p-2">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={2}
          placeholder="Add a note — a gate code, a tech request, a heads-up… Pegasus reads it and may surface it as a pill."
          className="w-full resize-none bg-transparent px-1.5 py-1 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none"
        />
        <div className="flex items-center justify-between gap-2 pt-1">
          <InfoTip label="Where notes go">Saved in Pegasus and read by the AI. The ESC history stays untouched.</InfoTip>
          <button
            type="button"
            onClick={submit}
            disabled={!body.trim() || add.isPending}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800 disabled:bg-brand-300"
          >
            {add.isPending ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving &amp; reading…
              </>
            ) : (
              "Add note"
            )}
          </button>
        </div>
      </div>

      {notes.length > 0 && (
        <ul className="mt-3 space-y-2.5">
          {notes.map((n) => (
            <li key={n.id} className="group flex items-start gap-2.5">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand-500">
                <NotebookPen className="h-3 w-3" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="whitespace-pre-wrap text-sm text-slate-800">
                  {n.body}
                </p>
                <p className="text-[11px] text-slate-400">
                  {n.author ?? "Office"} ·{" "}
                  {new Date(n.created_at).toLocaleString(undefined, {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </p>
              </div>
              {/* 360-AUDIT #12: destructive + confirm-less + invisible = bad
                  trio. Two-click confirm, visible at rest. */}
              {confirmDel === n.id ? (
                <span className="flex shrink-0 items-center gap-1">
                  <button type="button" onClick={() => { del.mutate({ id: n.id, custno }); setConfirmDel(null); }}
                    className="rounded bg-rose-600 px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-rose-700">
                    Delete
                  </button>
                  <button type="button" onClick={() => setConfirmDel(null)}
                    className="rounded px-1.5 py-0.5 text-[11px] text-slate-400 hover:text-slate-600">
                    Keep
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmDel(n.id)}
                  title="Delete note"
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-slate-300 opacity-40 transition focus-visible:opacity-100 group-hover:opacity-100 hover:bg-slate-100 hover:text-red-500"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {otherCount > 0 && (
        <p className="mt-2 border-t border-slate-100 pt-2 text-[11px] text-slate-400">
          {otherCount} more note{otherCount > 1 ? "s" : ""} on this customer&apos;s other properties — switch properties above to see them.
        </p>
      )}
    </Card>
  );
}

// ----------------------------------------------------------------------------
// AI Summary card
// ----------------------------------------------------------------------------

function AISummaryCard({ custno, locno }: { custno: string; locno: string }) {
  const { data, isLoading, isFetching, error, refetch } =
    useLocationNotesSummary(custno, locno);

  if (isLoading) {
    return (
      <Card className="p-5">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Sparkles className="h-3.5 w-3.5 animate-pulse text-brand-500" />
          Generating summary from notes, dispatches, and install history…
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50 p-5">
        <div className="flex items-start gap-2 text-sm">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" />
          <div className="flex-1">
            <p className="font-medium text-red-900">
              Couldn&apos;t generate AI summary
            </p>
            <p className="mt-1 break-words text-xs text-red-700">
              {(error as Error)?.message || "Unknown error"}
            </p>
            <button
              type="button"
              onClick={() => refetch()}
              className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-red-700 underline hover:text-red-900"
            >
              <RefreshCw className="h-3 w-3" /> Try again
            </button>
          </div>
        </div>
      </Card>
    );
  }

  if (!data?.summary) return null;

  return (
    <Card className="p-5">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-brand-500" />
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            AI Summary
          </span>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-1 text-[11px] text-slate-500 transition-colors hover:text-slate-600 disabled:cursor-wait"
          title={
            data.cached
              ? "Served from cache. Click to force a regeneration."
              : "Generated fresh."
          }
        >
          <RefreshCw
            className={cn("h-3 w-3", isFetching && "animate-spin")}
          />
          {data.cached ? "Cached" : "Just generated"} · {formatStamp(data.generated_at)}
        </button>
      </div>
      <div className="whitespace-pre-line text-sm leading-relaxed text-slate-700">
        {data.summary}
      </div>
    </Card>
  );
}

// ----------------------------------------------------------------------------
// Raw notes card
// ----------------------------------------------------------------------------

// ----------------------------------------------------------------------------
// ESC notes, READABLE (Seth 2026-08-21: "the raw ESC notes is tiny"). ESC
// stores decades of office notes as one text blob of stamped lines —
// "8/16/2026 2:55:36 PM - SETH - Previous owners were …". We parse those
// stamps into a real feed (date · author · note at body size) instead of a
// wall of 12px monospace. HONESTY RULES: entries keep the blob's own order,
// unstamped lines stay attached to the entry above them, and one click shows
// the untouched original — the parsed view is a lens, never a rewrite.
// ----------------------------------------------------------------------------

interface EscEntry {
  stamp: string | null;
  when: Date | null;
  author: string | null;
  text: string;
}

const ESC_ENTRY_RE =
  /^\s*(\d{1,2}\/\d{1,2}\/\d{2,4})(?:,?\s+(\d{1,2}:\d{2}(?::\d{2})?\s*(?:AM|PM)?))?\s*-\s*([A-Za-z][A-Za-z0-9 .'_-]{0,29}?)\s*-\s*(.*)$/i;

/** Split the ESC notes blob into stamped entries; null = format not found. */
function parseEscNotes(raw: string): EscEntry[] | null {
  const entries: EscEntry[] = [];
  let cur: EscEntry | null = null;
  let stamped = 0;
  for (const line of raw.split(/\r?\n/)) {
    const m = line.match(ESC_ENTRY_RE);
    if (m) {
      stamped++;
      const [, d, t, who, rest] = m;
      const when = new Date(t ? `${d} ${t}` : d);
      cur = {
        stamp: t ? `${d} ${t}` : d,
        when: isNaN(when.getTime()) ? null : when,
        author: who.trim() || null,
        text: rest.trim(),
      };
      entries.push(cur);
    } else if (line.trim()) {
      // Continuation line — belongs to the entry above (or a preamble).
      if (cur) cur.text += (cur.text ? "\n" : "") + line.trim();
      else {
        cur = { stamp: null, when: null, author: null, text: line.trim() };
        entries.push(cur);
      }
    }
  }
  // Only claim "parsed" when the stamped format is really there.
  return stamped >= 2 ? entries : null;
}

const SHOW_FIRST = 10;

function RawNotesCard({ location }: { location: LocationRow }) {
  const hasNotes = !!(location.notes && location.notes.trim());
  const customRows = customFieldRows(location);
  const hasCustom = customRows.length > 0;
  const [showRaw, setShowRaw] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [q, setQ] = useState("");

  const entries = hasNotes ? parseEscNotes(location.notes!) : null;
  const filtered =
    entries && q.trim()
      ? entries.filter((e) =>
          `${e.text} ${e.author ?? ""} ${e.stamp ?? ""}`.toLowerCase().includes(q.trim().toLowerCase()),
        )
      : entries;
  const visible = filtered ? (showAll || q.trim() ? filtered : filtered.slice(0, SHOW_FIRST)) : null;

  if (!hasNotes && !hasCustom) {
    return (
      <Card className="p-5">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
          <FileText className="h-3.5 w-3.5" />
          ESC Notes
        </div>
        <p className="mt-3 text-sm italic text-slate-500">
          No ESC notes or custom fields on file for this location.
        </p>
      </Card>
    );
  }

  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
          <FileText className="h-3.5 w-3.5" />
          ESC Notes
        </span>
        {entries && (
          <span className="text-[11px] text-slate-400">{entries.length} entries · read-only</span>
        )}
        <span className="ml-auto flex items-center gap-2">
          {entries && entries.length > 6 && (
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search notes…"
              className="w-36 rounded-md border border-hairline px-2 py-1 text-xs focus:border-brand-400 focus:outline-none"
            />
          )}
          {entries && (
            <button
              type="button"
              onClick={() => setShowRaw((v) => !v)}
              className="text-[11px] font-medium text-slate-400 underline-offset-2 transition-colors hover:text-slate-600 hover:underline"
              title="The untouched ESC text, exactly as stored"
            >
              {showRaw ? "Readable view" : "View original"}
            </button>
          )}
        </span>
      </div>

      {hasNotes && (!entries || showRaw) && (
        <div className="mt-3 whitespace-pre-wrap rounded-md bg-slate-50 p-3 text-[13px] leading-relaxed text-slate-700">
          {location.notes}
        </div>
      )}

      {hasNotes && entries && !showRaw && (
        <>
          <ul className="mt-3 divide-y divide-slate-50">
            {(visible ?? []).map((e, i) => (
              <li key={i} className="flex gap-3 py-2.5 first:pt-1">
                <span className="w-[74px] shrink-0 pt-0.5 text-right">
                  <span className="block text-xs font-semibold tabular-nums text-slate-600">
                    {e.when ? format(e.when, "MMM d, yyyy") : (e.stamp ?? "—")}
                  </span>
                  {e.when && e.stamp && e.stamp.includes(":") && (
                    <span className="block text-[11px] tabular-nums text-slate-400">{format(e.when, "h:mm a")}</span>
                  )}
                  {e.author && (
                    <span className="mt-0.5 inline-block max-w-full truncate rounded bg-slate-100 px-1 py-px text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                      {e.author}
                    </span>
                  )}
                </span>
                <p className="min-w-0 flex-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-800">{e.text}</p>
              </li>
            ))}
          </ul>
          {q.trim() && (filtered?.length ?? 0) === 0 && (
            <p className="py-3 text-center text-xs text-slate-400">Nothing matches &ldquo;{q.trim()}&rdquo;.</p>
          )}
          {!showAll && !q.trim() && (filtered?.length ?? 0) > SHOW_FIRST && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="mt-1 text-xs font-medium text-brand-600 transition-colors hover:text-brand-700"
            >
              Show all {filtered!.length} entries
            </button>
          )}
        </>
      )}

      {hasCustom && (
        <div className="mt-4 space-y-1 border-t border-slate-100 pt-3">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Custom fields
          </p>
          {customRows.map((row) => (
            <div
              key={row.key}
              className="flex gap-2 text-xs"
            >
              <span className="w-20 flex-shrink-0 font-mono text-slate-500">
                {row.key}
              </span>
              <span className="text-slate-700">{row.value}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function customFieldRows(location: LocationRow): { key: string; value: string }[] {
  const rows: { key: string; value: string }[] = [];
  const loc = location as unknown as Record<string, unknown>;
  for (let i = 1; i <= 16; i++) {
    const v = loc[`custom_${i}`];
    if (typeof v === "string" && v.trim()) {
      rows.push({ key: `custom_${i}`, value: v.trim() });
    }
  }
  return rows;
}

function formatStamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
